import type { Actor } from '@menage/api/types';
import { hasPermission, STAFF_ROLES, type Permission } from '@menage/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { configureApi, get } from '../api';
import { createAuthClient, type AuthClient } from './clients';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: Status;
  actor: Actor | null;
  client: AuthClient;
  login: (email: string, password: string) => Promise<Actor>;
  register: (input: { fullName: string; email: string; password: string; phone?: string; language: 'fr' | 'en' }) => Promise<Actor>;
  devLogin: (email: string) => Promise<Actor>;
  logout: () => Promise<void>;
  refresh: () => Promise<Actor | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Single auth context for the whole app (Rise Up's per-component useAuth hook multiplied listeners). */
export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useMemo(() => createAuthClient(), []);
  const queryClient = useQueryClient();
  const [state, setState] = useState<{ status: Status; actor: Actor | null }>({ status: 'loading', actor: null });

  const logout = useCallback(async () => {
    await client.logout();
    queryClient.clear();
    setState({ status: 'anonymous', actor: null });
  }, [client, queryClient]);

  useEffect(() => {
    configureApi({ getToken: () => client.getToken(), onUnauthorized: () => void logout() });
  }, [client, logout]);

  const refresh = useCallback(async () => {
    configureApi({ getToken: () => client.getToken(), onUnauthorized: () => void logout() });
    if (!(await client.getToken())) {
      setState({ status: 'anonymous', actor: null });
      return null;
    }
    try {
      const { actor } = await get<{ actor: Actor }>('/auth/me');
      setState({ status: 'authenticated', actor });
      document.documentElement.lang = actor.language;
      return actor;
    } catch {
      await client.logout();
      setState({ status: 'anonymous', actor: null });
      return null;
    }
  }, [client, logout]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const after = useCallback(async () => {
    queryClient.clear();
    const actor = await refresh();
    if (!actor) throw new Error('login_failed');
    return actor;
  }, [queryClient, refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      client,
      login: async (email, password) => {
        await client.login(email, password);
        return after();
      },
      register: async (input) => {
        await client.register(input);
        return after();
      },
      devLogin: async (email) => {
        await client.devLogin(email);
        return after();
      },
      logout,
      refresh,
    }),
    [state, client, after, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** Authenticated actor (only inside protected routes). */
export function useActor(): Actor {
  const { actor } = useAuth();
  if (!actor) throw new Error('useActor without actor');
  return actor;
}

/** Permission checks: UI hides what the user cannot do; the API enforces it. */
export function useCan() {
  const { actor } = useAuth();
  return useCallback((p: Permission | readonly Permission[]) => (actor ? hasPermission(actor.permissions, p) : false), [actor]);
}

export function homePathFor(actor: Actor): string {
  if (STAFF_ROLES.includes(actor.role)) return '/admin';
  return actor.role === 'EMPLOYEE' ? '/employe' : '/client';
}
